import type { AdjustmentMode, SpecType } from '@local/lib';

import {
  BoundOperator,
  getAllowedFormulaTypes,
  getDefaultAdjustmentModeFromSpecType,
  ProductPriceBasisType,
  QualityFormulaType,
  TierMode,
} from '@local/lib';

import type {
  ProductPriceBasisState,
  RangeFormulaRow,
  SingleValueFormulaRow,
  SpecState,
  ThresholdFormulaRow,
} from './qualityFormulaTypes';

import { isMultiRowFormulaType, SINGLE_ROW_CONFIG_BY_FORMULA_TYPE, SPEC_TYPES } from './qualityFormulaMetadata';

// Fields shared by every Threshold/Range row (rate fields + adjustmentMode) — bounds are added on
// top for Range rows only (see createRangeRowShape), mirroring how the API's own row schemas
// share a base shape (rangeRowBaseShape/thresholdSideBaseShape in @local/lib's QualityDto.ts).
const createThresholdRowShape = (specType: SpecType): Omit<ThresholdFormulaRow, 'id'> => ({
  adjustmentMode: getDefaultAdjustmentModeFromSpecType(specType),
  isFlatAdjustment: false,
  percentFactor: 1,
  perIncrement: 1,
  qualityPricePerUnit: 0.01,
});

const createRangeRowShape = (specType: SpecType): Omit<RangeFormulaRow, 'id'> => ({
  ...createThresholdRowShape(specType),
  // A freshly created row's bound is purely a tier-selection floor now — it no longer feeds any
  // division, so it can safely default to 0 for every spec type.
  lowerBound: 0,
  lowerBoundOperator: BoundOperator.Exclusive,
  upperBound: null,
  upperBoundOperator: BoundOperator.Inclusive,
});

const createSingleValueRowShape = (formulaType: QualityFormulaType): Omit<SingleValueFormulaRow, 'id'> => {
  switch (formulaType) {
    case QualityFormulaType.AdjustedSalesPrice: {
      const { fallback } = SINGLE_ROW_CONFIG_BY_FORMULA_TYPE[formulaType];
      return { contractedSpec: fallback, lowerBound: 0 };
    }
    case QualityFormulaType.QuantityAdjustedToContracted:
    case QualityFormulaType.QuantityAdjustedToDry: {
      const { fallback } = SINGLE_ROW_CONFIG_BY_FORMULA_TYPE[formulaType];
      return { contractedSpec: 0, lowerBound: fallback };
    }
    // QuantityAdjustedByPercent has no configurable row field at all — no contracted baseline,
    // no tier — so both fields are unused placeholders.
    case QualityFormulaType.Percentage:
    case QualityFormulaType.PerUnit:
    case QualityFormulaType.QuantityAdjustedByPercent:
    default:
      return { contractedSpec: 0, lowerBound: 0 };
  }
};

// Generates a fresh id per row so list rendering (React key) never relies on array index — each
// call (initial state, formula-type switch, add-row) produces a distinct row identity.
export const makeNewThresholdRow = (specType: SpecType): ThresholdFormulaRow => ({
  ...createThresholdRowShape(specType),
  id: crypto.randomUUID(),
});

export const makeNewRangeRow = (specType: SpecType): RangeFormulaRow => ({
  ...createRangeRowShape(specType),
  id: crypto.randomUUID(),
});

export const makeNewSingleValueRow = (formulaType: QualityFormulaType): SingleValueFormulaRow => ({
  ...createSingleValueRowShape(formulaType),
  id: crypto.randomUUID(),
});

/** A fresh "Contract Price" basis — the long-standing default every spec started with before Product Price Basis existed. */
export const createDefaultPriceBasis = (): ProductPriceBasisState => ({
  deemedPrice: 0,
  selectedComponentKeys: [],
  type: ProductPriceBasisType.ContractPrice,
});

// Defaults to the spec type's own first allowed formula type (e.g. Nitrogen only ever allows
// QuantityAdjustedByPercent) rather than always Percentage, so opening a spec never shows a
// formula type it can't actually use.
export const createDefaultSpecState = (specType: SpecType): SpecState => {
  const [defaultFormulaType] = getAllowedFormulaTypes(specType);

  return {
    collapsed: false,
    contractedSpec: 0,
    enabled: false,
    formulaType: defaultFormulaType,
    precision: 5,
    priceBasis: createDefaultPriceBasis(),
    rows: isMultiRowFormulaType(defaultFormulaType)
      ? [makeNewThresholdRow(specType)]
      : [makeNewSingleValueRow(defaultFormulaType)],
    tierMode: TierMode.Threshold,
  };
};

export const createInitialSpecStates = (): Record<SpecType, SpecState> =>
  SPEC_TYPES.reduce(
    (acc, spec) => {
      acc[spec.key] = createDefaultSpecState(spec.key);
      return acc;
    },
    {} as Record<SpecType, SpecState>,
  );

// Ensures one row exists per requested Threshold side, preserving each side's existing rate
// fields when it stays selected — a side has no bounds to derive (see rowUsesLowerBoundAsContractedSpec
// for the presentation-only direction label), so this is just add/keep/drop by adjustmentMode.
export const buildThresholdSideRows = (
  specType: SpecType,
  sides: AdjustmentMode[],
  existingRows: ThresholdFormulaRow[],
): ThresholdFormulaRow[] =>
  sides.map(
    adjustmentMode =>
      existingRows.find(row => row.adjustmentMode === adjustmentMode) ?? {
        ...makeNewThresholdRow(specType),
        adjustmentMode,
      },
  );

// Regenerates every row's amount fields for a new formula type while preserving each row's
// bounds/adjustmentMode (tier structure) — used when switching PERCENTAGE <-> PER_UNIT.
export const regenerateRowsForFormulaType = (rows: RangeFormulaRow[], specType: SpecType): RangeFormulaRow[] =>
  rows.map(row => ({
    ...makeNewRangeRow(specType),
    adjustmentMode: row.adjustmentMode,
    id: row.id,
    lowerBound: row.lowerBound,
    lowerBoundOperator: row.lowerBoundOperator,
    upperBound: row.upperBound,
    upperBoundOperator: row.upperBoundOperator,
  }));

// A fresh, valid 2-row Range chain — used both when first switching into Range mode and when
// resetting entered values, since independently regenerating each row would leave duplicate,
// unlinked default bounds instead of a sensible starting split.
export const buildDefaultRangeRows = (specType: SpecType): RangeFormulaRow[] => {
  const midpoint = 10;
  return [
    { ...makeNewRangeRow(specType), upperBound: midpoint },
    { ...makeNewRangeRow(specType), lowerBound: midpoint },
  ];
};
